import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { EventKeyword, RecomInterests } from '@base/models/responses/recommendation-interests.res';
import { MeetService } from '@base/services/private/meet.service';
import { ModalService } from '@base/services/private/modal.service';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { ModalMeetFilterComponent } from '@base/components/private/modals/modal-meet-filter/modal-meet-filter.component';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { UpdateInterestParams } from '@base/models/requests/update-interest.req';
import { transformDataInterest } from '@base/utils';
import { InterestSelected } from '@base/models/others/interest-selected';
import { TypeOfInterest } from '@base/enums/type-interest.enum';
import { CountInterestsSelectedPipe } from '@base/pipes/private/ui.pipe';
import { GlobalStateService } from '@base/services/private/global-state.service';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { Subscription } from 'rxjs';
import { GlobalService } from '@base/services/private/global.service';
import { StatusResponse } from '@base/enums/status-response.enum';
import { ModalFooterComponent } from '@base/components/private/modals/modal-footer/modal-footer.component';
import { ModalConfig } from '@base/models/others/modal-config';
import { EntityApi } from '@base/enums/menu-config.enum';

@Component({
  selector: 'app-modal-meet-interest',
  imports: [
    ModalHeaderComponent,
    ModalBodyComponent,
    NgxSkeletonLoaderComponent,
    CountInterestsSelectedPipe,
    forwardRef(() => TranslatePipe),
    ModalFooterComponent,
  ],
  templateUrl: './modal-meet-interest.component.html',
  styleUrl: './modal-meet-interest.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalMeetInterestComponent implements OnInit, OnDestroy {
  private readonly modalSrv = inject(ModalService);
  private readonly meetSrv = inject(MeetService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly globalStateSrv = inject(GlobalStateService);
  private readonly globalSrv = inject(GlobalService);

  @Input() modalId = '';
  @Input() entity = EntityApi.COMPANY;

  interests: RecomInterests | null = null;
  general: EventKeyword[] = [];
  products: EventKeyword[] = [];
  selectedFilter: InterestSelected[] = [];

  isShowPlaceholder = true;
  isHttpRequestRunning = false;
  isHasMeetInterest = this.globalStateSrv.isHasMeetInterest();
  isHasRecommendation = this.globalStateSrv.isHasRecommendation();
  isHasIntendToMeet = this.globalStateSrv.isHasIntendToMeet();
  readonly errorMessage = signal('');
  readonly hasAttemptedSubmit = signal(false);

  httpRequestObserver$: Subscription | null = null;

  ngOnInit(): void {
    this.getRecomInterests();
    this.listenHttpRequestState();
  }

  ngOnDestroy(): void {
    this.httpRequestObserver$?.unsubscribe();
    this.isHttpRequestRunning = false;
    this.globalSrv.destroyModalRef(this.modalId);
  }

  /**
   * Listens for HTTP request state changes and updates the component state accordingly.
   * @returns void
   */
  listenHttpRequestState(): void {
    this.httpRequestObserver$ = this.globalSrv.httpRequestObserver().subscribe({
      next: (res) => {
        this.isHttpRequestRunning = res;
        this.triggerChangeDetection();
      },
    });
  }

  /**
   * Closes the modal by its ID.
   * @returns void
   */
  close(): void {
    this.modalSrv.close(this.modalId);
  }

  /**
   * Fetches recommendation interests and updates state.
   * @returns void
   */
  getRecomInterests(): void {
    this.meetSrv.getRecomInterests(this.entity).subscribe({
      next: (res) => {
        if (!res) return;

        this.interests = res;
        if (this.interests.event_keywords) {
          this.general = this.interests.event_keywords.filter((item) => item.term_type === TypeOfInterest.GENERAL);
          this.products = this.interests.event_keywords.filter((item) => item.term_type === TypeOfInterest.PRODUCT);
        }
        this.selectedFilter = this.doInitMapInterestSelected() || [];
        this.isShowPlaceholder = false;

        this.triggerChangeDetection();
      },
      error: (err) => {
        console.error('Error fetching recommendation interests:', err);
        this.isShowPlaceholder = false;
      },
    });
  }

  /**
   * Maps initial selected interests from fetched data.
   * @returns InterestSelected[] | undefined - The mapped selected interests or undefined.
   */
  doInitMapInterestSelected() {
    const selectedInterest: InterestSelected[] = [];
    if (!this.interests) return;

    const { event_keywords: eventKeywords, my_keywords: myKeywords } = this.interests;

    eventKeywords.forEach((event) => {
      if (!event.value.length) return;

      myKeywords.forEach((keyword) => {
        // Term IDs can be reused across categories.
        if (keyword.term_id === event.term_id && keyword.term_type === event.term_type) {
          keyword.value.forEach((val) => {
            selectedInterest.push({
              type: 'attribute',
              id: keyword.term_id,
              value: val,
              term_type: keyword.term_type as TypeOfInterest,
              subparent: '',
              parent: keyword.term,
            });
          });
        }

        // Nested match by title_id
        if (typeof event.term_id === 'string') {
          event.value.forEach((val) => {
            if (val.title_id === keyword.term_id && keyword.term_type === event.term_type) {
              keyword.value.forEach((val2) => {
                selectedInterest.push({
                  type: 'attribute',
                  id: keyword.term_id,
                  value: val2,
                  term_type: keyword.term_type as TypeOfInterest,
                  subparent: val.title,
                  parent: event.term_public,
                });
              });
            }
          });
        }
      });
    });

    return selectedInterest;
  }

  /**
   * Opens a modal filter for the selected interest detail.
   * @param detail - The event keyword detail to filter.
   * @returns void
   */
  showModalFilter(detail: EventKeyword): void {
    const data: Partial<ModalMeetFilterComponent> = {
      detail: detail,
      selectedFilter: this.selectedFilter,
      submitWord: 'ADD',
    };

    const config: ModalConfig = {
      backdrop: 'static',
      keyboard: false,
    };

    const modalRef = this.modalSrv.showModalMeetFilter({
      addsData: data,
      addsConfig: config,
    });

    modalRef?.closed.subscribe({
      next: (res: { key: string; data: InterestSelected[] }) => {
        if (!res) return;

        if (res.key === 'MODAL_FILTER') {
          this.selectedFilter = [...res.data]; // to trigger pipe to counting additional filter
          if (this.hasAttemptedSubmit()) {
            // Keep error message in sync after first submit attempt.
            this.refreshValidationMessage();
          }
          this.changeDetectorRef.markForCheck();
          this.changeDetectorRef.detectChanges();
        }
      },
      error: (err) => {
        console.error('Error showing modal filter:', err);
      },
    });
  }

  /**
   * Shows a modal warning if the account is a preview account and status response is -2.
   */
  showModalWarning(): void {
    this.modalSrv.showModalWarningForPreviewAccount();
    return;
  }

  /**
   * Offered categories (general/product) with zero selections. Unoffered categories are skipped.
   * @returns TypeOfInterest[] - Offered categories still needing a selection.
   */
  private get emptyInterestCategories(): TypeOfInterest[] {
    const hasSelection = (termType: TypeOfInterest) => this.selectedFilter.some((item) => item.term_type === termType);

    const empty: TypeOfInterest[] = [];
    if (this.general.length > 0 && !hasSelection(TypeOfInterest.GENERAL)) {
      empty.push(TypeOfInterest.GENERAL);
    }
    if (this.products.length > 0 && !hasSelection(TypeOfInterest.PRODUCT)) {
      empty.push(TypeOfInterest.PRODUCT);
    }

    return empty;
  }

  /**
   * True once interests load and neither category is offered (shows `NO_DATA_AVAILABLE`, hides submit).
   * @returns boolean
   */
  get hasNoInterestCategory(): boolean {
    return !!this.interests && this.general.length === 0 && this.products.length === 0;
  }

  /**
   * True when every offered category has a selection (backend drops empty categories silently).
   * @returns boolean
   */
  get isInterestSelectionValid(): boolean {
    return this.emptyInterestCategories.length === 0;
  }

  /**
   * Translation key describing which offered category(ies) still need a selection.
   * @returns string - i18n key for the specific empty category, or both.
   */
  private get interestValidationMessageKey(): string {
    const empty = this.emptyInterestCategories;
    if (empty.length === 2) {
      return 'INTEREST_SELECT_BOTH';
    }

    return empty[0] === TypeOfInterest.PRODUCT ? 'INTEREST_SELECT_PRODUCT' : 'INTEREST_SELECT_GENERAL';
  }

  /**
   * Points the error message at whatever is still missing, or clears it once nothing is.
   * @returns void
   */
  private refreshValidationMessage(): void {
    this.errorMessage.set(this.isInterestSelectionValid ? '' : this.interestValidationMessageKey);
  }

  /**
   * Updates the user's selected interests by sending them to the server.
   * Closes the modal and triggers a refetch if update is successful.
   * @returns void
   */
  doUpdateInterests(): void {
    this.hasAttemptedSubmit.set(true);
    this.refreshValidationMessage();

    if (!this.isInterestSelectionValid) return;

    const general = transformDataInterest(
      this.selectedFilter.filter((item) => item.term_type === TypeOfInterest.GENERAL)
    );
    const product = transformDataInterest(
      this.selectedFilter.filter((item) => item.term_type === TypeOfInterest.PRODUCT)
    );

    // Guard against a no-op payload when no category is offered at all.
    if (Object.values(general).length === 0 && Object.values(product).length === 0) return;

    const data: UpdateInterestParams = {
      intendToMeet: {
        general,
        product,
      },
    };
    this.meetSrv.postUpdateInterest(data, this.entity).subscribe({
      next: (res) => {
        if (res.status === StatusResponse.SUCCESS) {
          this.close();
          this.globalStateSrv.doRefetch(true, 'MEET');
          this.globalStateSrv.doRefetch(true, 'MEET-FORYOU');
        } else if (+res.status === StatusResponse.PREVIEW_ACCOUNT) {
          // Preview account returns status -2, but success returns 'SUCCESS'; always convert to number to check for -2.
          this.showModalWarning();
        }
      },
      error: (err) => {
        console.error('Error updating interests:', err);
      },
    });
  }

  /**
   * Triggers change detection for the component.
   * @returns void
   */
  triggerChangeDetection() {
    this.changeDetectorRef.detectChanges();
    this.changeDetectorRef.markForCheck();
  }
}
